import React from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { createNewSession } from 'flow/actions/chat-actions';
import SenderAvatar from 'flow/components/tickets/internal-note/SenderAvatar';
import { useRowTabIndex } from 'flow/components/ui-components/row-tab-stop';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectChatUserKey,
    selectContact,
} from 'flow/selectors/ExtensionSelectors';
import { ChatSessionKey, ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { State } from 'flow/types/State';
import { extractName } from 'flow/utils';
import { setRecentColleague } from 'flow/utils/browser-utils';

type Props = {
    extensionKey: ExtensionKey;
};

/** A colleague there is no chat with yet, offered while searching. It reads its
 *  own contact and chat-user key, so the list around it needs no hold on the
 *  extensions map for a value only these rows use. */
const NewChatListItem = ({ extensionKey }: Props) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const rowTabIndex = useRowTabIndex();

    const contact = useAppSelector((state: State) =>
        selectContact(state, extensionKey),
    );
    const chatUserKey = useAppSelector((state: State) =>
        selectChatUserKey(state, extensionKey),
    );

    const handleNewChat = () => {
        const onSuccess = (chatSessionKey: ChatSessionKey) => {
            history.push(
                generatePath(routes['messages.id'], { id: chatSessionKey }),
            );
        };

        dispatch(createNewSession(chatUserKey, onSuccess));
        setRecentColleague(extensionKey);
    };

    return (
        <button
            type="button"
            className="focus-ring-inset flex h-13 w-full items-center gap-x-3 rounded-sm p-2 hover:bg-gray-200"
            onClick={handleNewChat}
            tabIndex={rowTabIndex}
        >
            <SenderAvatar sender={extensionKey} />

            <p className="line-clamp-2 text-start text-base/5">
                {extractName(contact)}
            </p>
        </button>
    );
};

export default React.memo(NewChatListItem);
